import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Modal from "@mui/material/Modal";
import { RxCross1 } from "react-icons/rx";
import { ReactComponent as ImportIcon } from "../../assets/icons/import.svg";

function EditMetricModal({
  open,
  onClose,
  editData,
  onUpdate,
  showImportModal,
  onCloseImportModal,
  onImport,
}) {
  const [form, setForm] = useState({
    metricId: "",
    dateOfImport: "",
    assigned: false,
  });

  // Import modal state (single file)
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (editData) {
      setForm({
        metricId: editData.metricId,
        dateOfImport: editData.dateOfImport,
        assigned: editData.assigned,
      });
    }
  }, [editData, open]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (file) {
      setSelectedFile(file);
      importFormik.setFieldValue("file", file);
    }
  };

  const handleCancelImport = () => {
    setSelectedFile(null);
    importFormik.resetForm();
    onCloseImportModal();
  };

  // Formik validation schema for single file
  const importValidationSchema = Yup.object({
    file: Yup.mixed()
      .required("Please select a file")
      .test(
        "fileType",
        "Only Excel files (.xlsx, .xls) are allowed",
        (value) => {
          if (!value) return false;
          const allowedTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
          ];
          return allowedTypes.includes(value.type);
        }
      ),
  });

  const importFormik = useFormik({
    initialValues: {
      file: null,
    },
    validationSchema: importValidationSchema,
    onSubmit: async (values) => {
      try {
        await onImport(values.file);
        setSelectedFile(null);
        importFormik.resetForm();
        onCloseImportModal();
      } catch (error) {
        toast.error("Import failed. Please try again.");
      }
    },
  });

  return (
    <>
      {/* Import Modal */}
      <Modal
        open={showImportModal}
        onClose={onCloseImportModal}
        aria-labelledby="import-modal-title"
        aria-describedby="import-modal-description"
        className="department-modal"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Import File</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={handleCancelImport} />
            </div>
          </div>
          <div className="modal_content">
            <form onSubmit={importFormik.handleSubmit}>
              <div className="form_field_wrapper">
                <div
                  style={{
                    border: "2px dashed #d0d0d0",
                    borderRadius: 8,
                    padding: "48px 24px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: "#fafafa",
                    minHeight: 180,
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {!selectedFile ? (
                    <>
                      <div style={{ marginBottom: 12 }}>
                        <ImportIcon />
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "#666",
                          fontWeight: 500,
                          marginBottom: 4,
                        }}
                      >
                        Upload File
                      </div>
                      <div style={{ fontSize: 12, color: "#999" }}>
                        click to browse, Excel files only (.xlsx, .xls)
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: 12 }}>
                        <ImportIcon />
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          color: "#00A272",
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        {selectedFile.name}
                      </div>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        Click to change file
                      </div>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </div>
                {importFormik.errors.file && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontSize: 12,
                      marginTop: 8,
                      textAlign: "center",
                    }}
                  >
                    {importFormik.errors.file}
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className="modal_footer">
            <button
              type="button"
              onClick={handleCancelImport}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={importFormik.handleSubmit}
              disabled={!selectedFile || importFormik.isSubmitting}
              className="btn btn-primary"
            >
              {importFormik.isSubmitting ? "Importing..." : "Import"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="edit-modal-title"
        aria-describedby="edit-modal-description"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Edit Metric ID</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={onClose} />
            </div>
          </div>
          <div className="modal_content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onUpdate({ ...editData, ...form });
                onClose();
              }}
            >
              <div className="form_field_wrapper">
                <label>Metric ID</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={form.metricId}
                  onChange={(e) =>
                    setForm({ ...form, metricId: e.target.value })
                  }
                />
              </div>
              <div className="form_field_wrapper">
                <label>Date of Import</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={form.dateOfImport}
                  onChange={(e) =>
                    setForm({ ...form, dateOfImport: e.target.value })
                  }
                />
              </div>
              <div className="form_field_wrapper">
                <label style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    style={{ marginRight: 8, width: 18, height: 18 }}
                    checked={form.assigned}
                    onChange={(e) =>
                      setForm({ ...form, assigned: e.target.checked })
                    }
                  />
                  Assigned
                </label>
              </div>
            </form>
          </div>
          <div className="modal_footer">
            <button type="button" onClick={onClose} className="btn btn-border">
              Cancel
            </button>
            <button
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                onUpdate({ ...editData, ...form });
                onClose();
              }}
              className="btn btn-primary"
            >
              Update
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default EditMetricModal;
